import { useMemo } from 'react';

import {
  ITEM_COMPARISON_STATUS,
  PackageItemComparison,
} from '@/model/CaseResolution/PackageItemComparison';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import {
  PACKAGE_ITEM_RESOLUTION,
  PACKAGE_ITEM_STOCK_RESOLUTION,
} from '@/model/Logistic/PackageItem/PackageItemResolution';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface CaseResolutionItem {
  itemComparisons: PackageItemComparison[];
  nonReturnItems: PackageItem[];
  receptionForRevisionComparisons: PackageItemComparison[];
}

const useCaseResolutionItems = (
  packageItems: PackageItem[],
  serviceOrderItems: ServiceOrderItem[],
) => {
  const { packageItemRepository } = useRepositoriesContext();
  /**
   * Metadata:
   * - Proposito: detectar casos de peritaje/preventivo sin recepcion donde el
   *   tecnico conserva el item segun la OT.
   * - Regla: se considera resuelto automaticamente cuando no hay devolucion
   *   (`to_return_amount === 0`) y el tecnico conserva toda la cantidad enviada
   *   (`stock_technician_amount >= sentAmount`).
   */
  const isAutoResolvedByServiceOrder = (
    comparison: PackageItemComparison,
    serviceOrderItem?: ServiceOrderItem,
  ) => {
    if (!serviceOrderItem || comparison.receptionItem) {
      return false;
    }

    const stockTechnicianAmount = serviceOrderItem.stock_technician_amount ?? 0;
    const toReturnAmount = serviceOrderItem.to_return_amount ?? 0;

    return toReturnAmount === 0 && stockTechnicianAmount >= (comparison.sentAmount ?? 0);
  };

  const caseResolutionItems: {
    itemComparisons: PackageItemComparison[];
    nonReturnItems: PackageItem[];
    receptionForRevisionComparisons: PackageItemComparison[];
  } = useMemo(() => {
    const { itemComparisons, nonReturnItems, receptionForRevisionComparisons } =
      packageItemRepository.buildPackageItemsForResolution(packageItems);

    itemComparisons.forEach(comparison => {
      if (comparison.shipmentItem) {
        const serviceOrderItem = serviceOrderItems.find(soi =>
          typeof soi.package_item === 'number'
            ? soi.package_item === comparison.shipmentItem?.id
            : soi.package_item?.id === comparison.shipmentItem?.id,
        );

        comparison.serviceOrderItem = serviceOrderItem;
        if (isAutoResolvedByServiceOrder(comparison, serviceOrderItem)) {
          // Se resuelve automaticamente: no habrá recepción y la OT ya define
          // que el material queda en stock del técnico.
          comparison.status = ITEM_COMPARISON_STATUS.CORRECT;
          comparison.resolution_status = PACKAGE_ITEM_RESOLUTION.RESOLUCION_AUTOMATICA;
          comparison.stock_resolution_status = PACKAGE_ITEM_STOCK_RESOLUTION.RESUELTO;
        }
      }
    });

    return { itemComparisons, nonReturnItems, receptionForRevisionComparisons };
  }, [packageItemRepository, packageItems, serviceOrderItems]);

  return { caseResolutionItems };
};

export default useCaseResolutionItems;
